import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Row,
	Select,
	notification,
} from "antd";

import { POST } from "../../../../providers/useAxiosQuery";

export default function RenderSelect(props) {
	const {
		namePath = [],
		form_question_options = [],
		required = false,
		question = null,
		description = null,
		add_another = false,
	} = props;

	let newNamePath = Array.isArray(namePath) ? namePath.join("_") : namePath;
	const form = Form.useFormInstance();
	const [inputs, setInputs] = useState(add_another ? [{ id: 0 }] : []);

	const baseOptions = useMemo(
		() =>
			form_question_options
				.filter((x) => !x.is_other)
				.map((item, index) => ({
					key: item.id ?? index,
					value: item.option,
					label: item.option,
				})),
		[form_question_options],
	);
	const [customOptions, setCustomOptions] = useState([]);
	const options = useMemo(() => {
		const existing = new Set(baseOptions.map((item) => item.value));
		return [
			...baseOptions,
			...customOptions.filter((item) => !existing.has(item.value)),
		];
	}, [baseOptions, customOptions]);
	const [otherValue, setOtherValue] = useState("");

	let findOther = form_question_options.find((option) => option.is_other);

	const { mutate: mutateAddOther, isLoading: isLoadingAddOther } = POST(
		"api/form_question_option_add_other",
		null,
		false,
	);

	const addLocalOption = (value) => {
		setCustomOptions((prev) =>
			prev.some((item) => item.value === value)
				? prev
				: [...prev, { key: `custom_${value}`, value, label: value }],
		);
	};

	const handleAddOption = (fieldName, value) => {
		const optionName = value.trim();
		if (!optionName || !findOther) return;

		mutateAddOther(
			{ other_option_id: findOther.id, option: optionName },
			{
				onSuccess: (res) => {
					if (res.success && res.data) {
						const newOption = res.data;

						// QuestionContent looks up sub options from this same array
						if (!form_question_options.some((o) => o.id === newOption.id)) {
							const otherIndex = form_question_options.indexOf(findOther);
							form_question_options.splice(
								otherIndex >= 0 ? otherIndex : form_question_options.length,
								0,
								newOption,
							);
						}

						addLocalOption(newOption.option);
						form.setFieldValue(fieldName, newOption.option);
						setOtherValue("");
					} else {
						notification.error({
							message: "Option",
							description: res.message,
						});
					}
				},
				onError: () => {
					// Offline or server error: keep the answer locally so the survey can continue
					addLocalOption(optionName);
					form.setFieldValue(fieldName, optionName);
					setOtherValue("");
				},
			},
		);
	};

	const handleAddAnother = () => {
		setInputs((prev) => [...prev, { id: prev.length }]);
	};

	// If add_another is enabled, render for each input set
	if (add_another) {
		return (
			<>
				{inputs.map((input, idx) => (
					<Col
						xs={24}
						sm={24}
						md={24}
						lg={24}
						xl={24}
						key={`input_set_${input.id}`}
					>
						<Row>
							<Col xs={24} sm={24} md={24} lg={24} xl={24}>
								<div
									className="quill-output question"
									dangerouslySetInnerHTML={{
										__html: question,
									}}
								/>
								{description && (
									<div className="description">{description}</div>
								)}
							</Col>
						</Row>
						<Row>
							<Col xs={24} sm={24} md={24} lg={24} xl={24}>
								<RenderSelectField
									fieldName={`${newNamePath}_set_${idx}`}
									required={required}
									options={options}
									findOther={findOther}
									otherValue={otherValue}
									setOtherValue={setOtherValue}
									onAddOption={handleAddOption}
									isLoadingAddOption={isLoadingAddOther}
								/>
							</Col>
						</Row>
					</Col>
				))}

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Button
						type="link"
						className="w-auto h-auto p-0"
						icon={<FontAwesomeIcon icon={faPlus} />}
						onClick={handleAddAnother}
					>
						Add Another
					</Button>
				</Col>
			</>
		);
	}

	return (
		<Row>
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				<div
					className="quill-output question"
					dangerouslySetInnerHTML={{
						__html: question,
					}}
				/>
				{description && <div className="description">{description}</div>}
			</Col>
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				<RenderSelectField
					fieldName={newNamePath}
					required={required}
					options={options}
					findOther={findOther}
					otherValue={otherValue}
					setOtherValue={setOtherValue}
					onAddOption={handleAddOption}
					isLoadingAddOption={isLoadingAddOther}
				/>
			</Col>
		</Row>
	);
}

const RenderSelectField = (props) => {
	const {
		fieldName,
		required,
		options,
		findOther,
		otherValue,
		setOtherValue,
		onAddOption,
		isLoadingAddOption,
	} = props;

	return (
		<Form.Item
			name={fieldName}
			rules={[
				{
					required: required ? true : false,
					message: "This field is required!",
				},
			]}
		>
			<Select
				placeholder="Select an answer"
				size="large"
				allowClear
				showSearch
				optionFilterProp="label"
				virtual
				listHeight={256}
				options={options}
				popupRender={
					findOther
						? (menu) => (
								<>
									{menu}
									<Divider style={{ margin: "5px 0" }} />
									<Flex style={{ padding: "0 8px 4px" }}>
										<Input
											placeholder="Add new option"
											value={otherValue}
											onChange={(e) => setOtherValue(e.target.value)}
											onKeyDown={(e) => {
												e.stopPropagation();
												if (e.key === "Enter" && !isLoadingAddOption) {
													e.preventDefault();
													onAddOption(fieldName, otherValue);
												}
											}}
											disabled={isLoadingAddOption}
										/>
										<Button
											type="text"
											icon={<FontAwesomeIcon icon={faPlus} />}
											loading={isLoadingAddOption}
											onClick={() => onAddOption(fieldName, otherValue)}
										/>
									</Flex>
								</>
						  )
						: undefined
				}
			/>
		</Form.Item>
	);
};
